// src/components/EditContractTemplate.tsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';

interface ContractSection {
    label: string;
    items: ContractItem[];
}

interface ContractItem {
    type: 'text' | 'field';
    label?: string;
    content?: string;
    value?: string;
    fillable?: boolean;
}

const EditContractTemplate: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [name, setName] = useState('');
    const [sections, setSections] = useState<ContractSection[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (id !== 'new') {
            fetchTemplate();
        }
    }, [id]);

    const fetchTemplate = async () => {
        setIsLoading(true);
        setError('');
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/ContractTemplates/${id}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setName(response.data.name);
            const parsedData = JSON.parse(response.data.jsonContent || '{"sections": []}');
            setSections(parsedData.sections || []);
        } catch (error: any) {
            setError('Failed to load contract template.');
        } finally {
            setIsLoading(false);
        }
    };

    const addSection = () => {
        setSections([...sections, { label: '', items: [] }]);
    };

    const updateSectionLabel = (index: number, label: string) => {
        const updatedSections = [...sections];
        updatedSections[index].label = label;
        setSections(updatedSections);
    };

    const addItem = (sectionIndex: number, type: 'text' | 'field') => {
        const updatedSections = [...sections];
        if (type === 'text') {
            updatedSections[sectionIndex].items.push({ type: 'text', content: '' });
        } else {
            updatedSections[sectionIndex].items.push({ type: 'field', label: '', value: '', fillable: false });
        }
        setSections(updatedSections);
    };

    const updateItem = (sectionIndex: number, itemIndex: number, field: string, value: string | boolean) => {
        const updatedSections = [...sections];
        updatedSections[sectionIndex].items[itemIndex] = {
            ...updatedSections[sectionIndex].items[itemIndex],
            [field]: value,
        };
        setSections(updatedSections);
    };

    const deleteItem = (sectionIndex: number, itemIndex: number) => {
        const updatedSections = [...sections];
        updatedSections[sectionIndex].items.splice(itemIndex, 1);
        setSections(updatedSections);
    };

    const deleteSection = (sectionIndex: number) => {
        const updatedSections = [...sections];
        updatedSections.splice(sectionIndex, 1);
        setSections(updatedSections);
    };

    const saveTemplate = async () => {
        setIsLoading(true);
        setError('');
        try {
            const token = localStorage.getItem('token');
            const templateData = { name, sections };
            if (id === 'new') {
                await axios.post(
                    `${process.env.REACT_APP_API_BASE_URL}/api/ContractTemplates`,
                    { name, jsonContent: JSON.stringify(templateData) },
                    { headers: { Authorization: `Bearer ${token}` } }
                );
            } else {
                await axios.put(
                    `${process.env.REACT_APP_API_BASE_URL}/api/ContractTemplates/${id}`,
                    { name, jsonContent: JSON.stringify(templateData) },
                    { headers: { Authorization: `Bearer ${token}` } }
                );
            }
            alert('Contract template saved successfully!');
            navigate('/contract-templates');
        } catch (error: any) {
            setError(error.response?.data || 'Failed to save contract template.');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="container mt-5">
            <h2 className="mb-4 text-center">{id === 'new' ? 'Create Contract Template' : 'Edit Contract Template'}</h2>
            {error && <div className="alert alert-danger">{error}</div>}
            {isLoading && <div className="alert alert-info">Loading...</div>}
            <div className="card shadow-sm mb-4">
                <div className="card-body">
                    <div className="mb-3">
                        <label className="form-label">Template Name</label>
                        <input
                            type="text"
                            className="form-control"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            disabled={isLoading}
                        />
                    </div>
                    <h4>Sections</h4>
                    {sections.map((section, sectionIndex) => (
                        <div key={sectionIndex} className="card mb-3">
                            <div className="card-body">
                                <div className="mb-3">
                                    <label className="form-label">Section Label</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={section.label}
                                        onChange={(e) => updateSectionLabel(sectionIndex, e.target.value)}
                                        disabled={isLoading}
                                    />
                                </div>
                                {section.items.map((item, itemIndex) => (
                                    <div key={itemIndex} className="border p-2 mb-2">
                                        {item.type === 'text' ? (
                                            <>
                                                <label className="form-label">Text Content</label>
                                                <textarea
                                                    className="form-control mb-2"
                                                    value={item.content}
                                                    onChange={(e) => updateItem(sectionIndex, itemIndex, 'content', e.target.value)}
                                                    disabled={isLoading}
                                                />
                                            </>
                                        ) : (
                                            <>
                                                <label className="form-label">Field Label</label>
                                                <input
                                                    type="text"
                                                    className="form-control mb-2"
                                                    value={item.label}
                                                    onChange={(e) => updateItem(sectionIndex, itemIndex, 'label', e.target.value)}
                                                    disabled={isLoading}
                                                />
                                                <label className="form-label">Default Value</label>
                                                <input
                                                    type="text"
                                                    className="form-control mb-2"
                                                    value={item.value}
                                                    onChange={(e) => updateItem(sectionIndex, itemIndex, 'value', e.target.value)}
                                                    disabled={isLoading}
                                                />
                                                <div className="form-check">
                                                    <input
                                                        type="checkbox"
                                                        className="form-check-input"
                                                        checked={item.fillable || false}
                                                        onChange={(e) => updateItem(sectionIndex, itemIndex, 'fillable', e.target.checked)}
                                                        disabled={isLoading}
                                                    />
                                                    <label className="form-check-label">Fillable (Vendor must fill this in)</label>
                                                </div>
                                            </>
                                        )}
                                        <button
                                            onClick={() => deleteItem(sectionIndex, itemIndex)}
                                            className="btn btn-danger btn-sm mt-2"
                                            disabled={isLoading}
                                        >
                                            Delete Item
                                        </button>
                                    </div>
                                ))}
                                <div className="d-flex gap-2 mt-2">
                                    <button
                                        onClick={() => addItem(sectionIndex, 'text')}
                                        className="btn btn-outline-primary btn-sm"
                                        disabled={isLoading}
                                    >
                                        Add Text Item
                                    </button>
                                    <button
                                        onClick={() => addItem(sectionIndex, 'field')}
                                        className="btn btn-outline-primary btn-sm"
                                        disabled={isLoading}
                                    >
                                        Add Field Item
                                    </button>
                                    <button
                                        onClick={() => deleteSection(sectionIndex)}
                                        className="btn btn-outline-danger btn-sm"
                                        disabled={isLoading}
                                    >
                                        Delete Section
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                    <div className="text-center mb-4">
                        <button onClick={addSection} className="btn btn-primary" disabled={isLoading}>
                            Add Section
                        </button>
                    </div>
                    <div className="text-center">
                        <button onClick={saveTemplate} className="btn btn-success" disabled={isLoading}>
                            {isLoading ? 'Saving...' : 'Save Template'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EditContractTemplate;